import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Notify from "../components/Notify";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import {
  notificationService,
  NotificationItem,
} from "../services";

interface NotificationSection {
  label: string;
  groups: NotificationItem[][];
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getDateLabel = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  return "Earlier";
};

const groupNotifications = (items: NotificationItem[]): NotificationSection[] => {
  const sectionMap = new Map<string, NotificationItem[]>();
  const sectionOrder: string[] = [];

  items.forEach((item) => {
    const label = getDateLabel(item.createdAt);
    if (!sectionMap.has(label)) {
      sectionMap.set(label, []);
      sectionOrder.push(label);
    }
    sectionMap.get(label)!.push(item);
  });

  return sectionOrder.map((label) => {
    const sectionItems = sectionMap.get(label)!;
    const groups: NotificationItem[][] = [];

    sectionItems.forEach((item) => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup[0].groupKey === item.groupKey) {
        lastGroup.push(item);
      } else {
        groups.push([item]);
      }
    });

    return { label, groups };
  });
};

const Notification = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
    }
  }, [navigate]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [listRes, unreadRes]: any[] = await Promise.all([
        notificationService.getNotifications({ page: 1, limit: 20 }),
        notificationService.getUnreadCount(),
      ]);

      if (listRes?.data) {
        setNotifications(listRes.data);
        if (listRes.pagination?.unreadCount != null) {
          setUnreadCount(listRes.pagination.unreadCount);
        }
      }

      if (unreadRes?.data?.unreadCount != null) {
        setUnreadCount(unreadRes.data.unreadCount);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const sections = useMemo(
    () => groupNotifications(notifications),
    [notifications]
  );

  const handleSelect = async (notification: NotificationItem) => {
    if (notification.readAt === null) {
      setNotifications((prev) =>
        prev.map((item) =>
          item._id === notification._id
            ? { ...item, readAt: new Date().toISOString() }
            : item
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        await notificationService.markAsRead(notification._id);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
        setNotifications((prev) =>
          prev.map((item) =>
            item._id === notification._id
              ? { ...item, readAt: null }
              : item
          )
        );
        setUnreadCount((prev) => prev + 1);
      }
    }

    if (notification.deepLink?.startsWith("/orders")) {
      navigate("/orders");
    }
  };

  const handleMarkAllAsRead = async () => {
    const unread = notifications.filter((n) => n.readAt === null);
    if (!unread.length || markingAll) return;

    setMarkingAll(true);
    const previous = notifications;
    const now = new Date().toISOString();

    setNotifications((prev) =>
      prev.map((item) =>
        item.readAt === null ? { ...item, readAt: now } : item
      )
    );
    setUnreadCount(0);

    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      setNotifications(previous);
      setUnreadCount(unread.length);
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="bg-[#FFF9F2] dark:bg-gray-900 min-h-screen transition-colors duration-300">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-[#FFF9F2] dark:bg-gray-900 z-50 px-4 py-4 transition-colors duration-300">
        <div className="relative flex items-center justify-center max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-0 w-10 h-10 flex items-center justify-center"
            aria-label="Go back"
          >
            <img src={arrowLeft} alt="back" className="w-6 h-6 dark:invert" />
          </button>
          <h1 className="text-gray-900 dark:text-white font-urbanist text-lg font-semibold">
            Notifications
          </h1>
        </div>
        {unreadCount > 0 && (
          <div className="max-w-4xl mx-auto flex justify-end mt-2 px-1">
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={markingAll}
              className="text-[#7402A7] font-urbanist text-sm font-medium disabled:opacity-60"
            >
              {markingAll ? "Marking..." : "Mark all as read"}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className={`px-4 pb-8 max-w-4xl mx-auto ${
          unreadCount > 0 ? "pt-24" : "pt-20"
        }`}
      >
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 border-2 border-[#7402A7] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm">
              No notifications yet
            </p>
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.label} className="mb-5">
              <h2 className="text-[#050505] dark:text-white font-urbanist text-xl font-bold mb-3">
                {section.label}
              </h2>
              <div className="space-y-3">
                {section.groups.map((group) => (
                  <Notify
                    key={group[0]._id}
                    notifications={group}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notification;
